import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Mountain,
  FileText,
  Home,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Search,
  Plus,
  Eye,
  Download,
  Send,
  Building2,
  MapPin,
  ChevronRight,
  Sparkles,
  CreditCard,
  FileCheck
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const ListingToolkitPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listingRequests, setListingRequests] = useState([]);
  const [titleRequests, setTitleRequests] = useState([]);
  const [fees, setFees] = useState({});
  const [properties, setProperties] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("requests");
  
  // New Request Form
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [newRequest, setNewRequest] = useState({
    property_id: "",
    request_type: "disclosure_packet",
    requester_type: "seller",
    requester_name: "",
    requester_email: "",
    requester_phone: "",
    requester_company: "",
    rush_processing: false,
    notes: ""
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestsRes, titleRes, feesRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/listing/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/listing/title-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/listing/fees`),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setListingRequests(requestsRes.data.requests || []);
      setTitleRequests(titleRes.data.requests || []);
      setFees(feesRes.data.fees || {});
      setProperties(propertiesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load listing toolkit data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequest.property_id || !newRequest.requester_name || !newRequest.requester_email) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/listing/requests`, newRequest);
      toast.success(`Request ${response.data.request_id} created. Total: $${response.data.total_amount}`);
      setShowNewRequest(false);
      setNewRequest({
        property_id: "",
        request_type: "disclosure_packet",
        requester_type: "seller",
        requester_name: "",
        requester_email: "",
        requester_phone: "",
        requester_company: "",
        rush_processing: false,
        notes: ""
      });
      fetchData();
    } catch (error) {
      console.error("Error creating request:", error);
      toast.error("Failed to create request");
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      await axios.put(
        `${API}/listing/requests/${requestId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleProcessTitleRequest = async (requestId, action) => {
    try {
      await axios.put(
        `${API}/listing/title-requests/${requestId}/process?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Request ${action} completed`);
      fetchData();
    } catch (error) {
      console.error("Error processing request:", error);
      toast.error("Failed to process request");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_payment: { color: "bg-yellow-100 text-yellow-800", label: "Pending Payment" },
      paid: { color: "bg-blue-100 text-blue-800", label: "Paid" },
      processing: { color: "bg-purple-100 text-purple-800", label: "Processing" },
      ready: { color: "bg-green-100 text-green-800", label: "Ready" },
      delivered: { color: "bg-gray-100 text-gray-800", label: "Delivered" },
      cancelled: { color: "bg-red-100 text-red-800", label: "Cancelled" },
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      approved: { color: "bg-blue-100 text-blue-800", label: "Approved" },
      payoff_generated: { color: "bg-green-100 text-green-800", label: "Payoff Ready" },
      completed: { color: "bg-gray-100 text-gray-800", label: "Completed" }
    };
    
    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const stats = [
    { label: "Pending Requests", value: listingRequests.filter(r => r.status === "pending_payment").length, icon: <Clock className="w-5 h-5" />, color: "text-yellow-600" },
    { label: "In Processing", value: listingRequests.filter(r => r.status === "processing").length, icon: <FileText className="w-5 h-5" />, color: "text-blue-600" },
    { label: "Ready for Pickup", value: listingRequests.filter(r => r.status === "ready").length, icon: <CheckCircle className="w-5 h-5" />, color: "text-green-600" },
    { label: "Title Requests", value: titleRequests.filter(r => r.status !== "completed").length, icon: <Building2 className="w-5 h-5" />, color: "text-purple-600" }
  ];

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Mountain className="w-12 h-12 text-primary mx-auto animate-pulse" />
            <p className="mt-4 text-muted-foreground">Loading Listing Toolkit...</p>
          </div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background" data-testid="listing-toolkit-page">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/board")}>
              <ChevronRight className="w-5 h-5 rotate-180" />
            </Button>
            <div>
              <h1 className="font-serif text-xl font-semibold">Listing Toolkit</h1>
              <p className="text-xs text-muted-foreground">Seller, Realtor & Title Company Support</p>
            </div>
          </div>
          <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
            <DialogTrigger asChild>
              <Button data-testid="new-listing-request-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Listing Request</DialogTitle>
                <DialogDescription>Create a new disclosure packet or status letter request</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Property *</Label>
                  <Select value={newRequest.property_id} onValueChange={(v) => setNewRequest({...newRequest, property_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(p => (
                        <SelectItem key={p.property_id} value={p.property_id}>
                          {p.address} (Lot {p.lot_number})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Request Type *</Label>
                    <Select value={newRequest.request_type} onValueChange={(v) => setNewRequest({...newRequest, request_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="disclosure_packet">Disclosure Packet</SelectItem>
                        <SelectItem value="status_letter">Status Letter</SelectItem>
                        <SelectItem value="estoppel_certificate">Estoppel Certificate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Requester Type *</Label>
                    <Select value={newRequest.requester_type} onValueChange={(v) => setNewRequest({...newRequest, requester_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seller">Seller</SelectItem>
                        <SelectItem value="realtor">Realtor</SelectItem>
                        <SelectItem value="title_company">Title Company</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input 
                      value={newRequest.requester_name}
                      onChange={(e) => setNewRequest({...newRequest, requester_name: e.target.value})}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={newRequest.requester_email}
                      onChange={(e) => setNewRequest({...newRequest, requester_email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={newRequest.requester_phone}
                      onChange={(e) => setNewRequest({...newRequest, requester_phone: e.target.value})}
                      placeholder="(970) 555-0100"
                    />
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Input 
                      value={newRequest.requester_company}
                      onChange={(e) => setNewRequest({...newRequest, requester_company: e.target.value})}
                      placeholder="ABC Realty"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea 
                    value={newRequest.notes}
                    onChange={(e) => setNewRequest({...newRequest, notes: e.target.value})}
                    placeholder="Any special instructions..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox"
                    id="rush"
                    checked={newRequest.rush_processing}
                    onChange={(e) => setNewRequest({...newRequest, rush_processing: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="rush" className="cursor-pointer">
                    Rush Processing (+${fees.rush_fee?.amount || 100})
                  </Label>
                </div>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-sm font-medium">Estimated Total</p>
                  <p className="text-2xl font-bold text-primary">
                    ${(fees[newRequest.request_type]?.amount || 150) + (newRequest.rush_processing ? (fees.rush_fee?.amount || 100) : 0)}
                  </p>
                </div>
                <Button className="w-full" onClick={handleCreateRequest} data-testid="submit-listing-request-btn">
                  Create Request
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={stat.color}>{stat.icon}</div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="requests" data-testid="listing-requests-tab">
              <FileText className="w-4 h-4 mr-2" />
              Listing Requests
            </TabsTrigger>
            <TabsTrigger value="title" data-testid="title-requests-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Title Company
            </TabsTrigger>
            <TabsTrigger value="fees" data-testid="fees-tab">
              <DollarSign className="w-4 h-4 mr-2" />
              Fee Schedule
            </TabsTrigger>
            <TabsTrigger value="buyer-info" data-testid="buyer-info-tab">
              <Users className="w-4 h-4 mr-2" />
              Buyer Info
            </TabsTrigger>
          </TabsList>

          {/* Listing Requests Tab */}
          <TabsContent value="requests">
            <Card>
              <CardHeader>
                <CardTitle>Listing Requests</CardTitle>
                <CardDescription>Disclosure packets, status letters, and estoppel certificates</CardDescription>
              </CardHeader>
              <CardContent>
                {listingRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No listing requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {listingRequests.map((req) => (
                      <div key={req.request_id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{req.request_id}</span>
                              {getStatusBadge(req.status)}
                              {req.rush_processing && <Badge variant="destructive">Rush</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {req.property_info?.address || "Property"} • {req.requester_type}
                            </p>
                            <p className="text-sm">{req.requester_name} ({req.requester_email})</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(req.created_at).toLocaleDateString()} • ${req.total_amount}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {req.status === "pending_payment" && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(req.request_id, "paid")}>
                                Mark Paid
                              </Button>
                            )}
                            {req.status === "paid" && (
                              <Button size="sm" onClick={() => handleUpdateStatus(req.request_id, "processing")}>
                                Start Processing
                              </Button>
                            )}
                            {req.status === "processing" && (
                              <Button size="sm" onClick={() => handleUpdateStatus(req.request_id, "ready")}>
                                Mark Ready
                              </Button>
                            )}
                            {req.status === "ready" && (
                              <Button size="sm" onClick={() => handleUpdateStatus(req.request_id, "delivered")}>
                                <Send className="w-4 h-4 mr-1" />
                                Deliver
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Title Company Tab */}
          <TabsContent value="title">
            <Card>
              <CardHeader>
                <CardTitle>Title Company Requests</CardTitle>
                <CardDescription>Payoff statements, closing letters, and transfer processing</CardDescription>
              </CardHeader>
              <CardContent>
                {titleRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No title company requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {titleRequests.map((req) => (
                      <div key={req.request_id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{req.request_id}</span>
                              {getStatusBadge(req.status)}
                            </div>
                            <p className="text-sm font-medium">{req.company_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {req.contact_name} • {req.property_address}
                            </p>
                            {req.closing_date && (
                              <p className="text-xs text-muted-foreground">
                                Closing: {new Date(req.closing_date).toLocaleDateString()}
                              </p>
                            )}
                            {req.payoff_amount && (
                              <p className="text-sm font-medium text-green-600 mt-1">
                                Payoff: ${req.payoff_amount.toFixed(2)}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {req.status === "pending" && (
                              <Button size="sm" onClick={() => handleProcessTitleRequest(req.request_id, "approve")}>
                                Approve
                              </Button>
                            )}
                            {req.status === "approved" && (
                              <Button size="sm" onClick={() => handleProcessTitleRequest(req.request_id, "generate_payoff")}>
                                Generate Payoff
                              </Button>
                            )}
                            {req.status === "payoff_generated" && (
                              <Button size="sm" onClick={() => handleProcessTitleRequest(req.request_id, "complete")}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fee Schedule Tab */}
          <TabsContent value="fees">
            <Card>
              <CardHeader>
                <CardTitle>Fee Schedule</CardTitle>
                <CardDescription>Current listing and transfer fees</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {Object.entries(fees).map(([key, fee]) => (
                    <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{fee.name}</p>
                        <p className="text-sm text-muted-foreground">{fee.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary">${fee.amount}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Buyer Info Tab */}
          <TabsContent value="buyer-info">
            <BuyerInfoSection />
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </AdminSidebar>
  );
};

// Buyer Info Section Component
const BuyerInfoSection = () => {
  const [buyerInfo, setBuyerInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBuyerInfo = async () => {
      try {
        const response = await axios.get(`${API}/listing/buyer-info`);
        setBuyerInfo(response.data);
      } catch (error) {
        console.error("Error fetching buyer info:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBuyerInfo();
  }, []);

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (!buyerInfo) return <div className="text-center py-8">Unable to load buyer information</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mountain className="w-5 h-5" />
            {buyerInfo.community?.name}
          </CardTitle>
          <CardDescription>{buyerInfo.community?.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Community Details</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>📍 {buyerInfo.community?.location}</li>
                <li>🏔️ Elevation: {buyerInfo.community?.elevation}</li>
                <li>🏠 Total Lots: {buyerInfo.community?.total_lots}</li>
                <li>📅 Established: {buyerInfo.community?.established}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Financial Information</h4>
              <ul className="space-y-1 text-sm">
                <li>Quarterly Dues: <strong>${buyerInfo.financial?.quarterly_dues}</strong></li>
                <li>Annual Total: <strong>${buyerInfo.financial?.annual_total}</strong></li>
                <li className="text-muted-foreground">Due dates: {buyerInfo.financial?.due_dates?.join(", ")}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Amenities</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {buyerInfo.amenities?.map((amenity, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">{amenity.name}</p>
                    <p className="text-xs text-muted-foreground">{amenity.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Key Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {buyerInfo.rules_highlights?.map((rule, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  {rule}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Metro District</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-medium">{buyerInfo.metro_district?.name}</p>
          <p className="text-sm text-muted-foreground mb-2">{buyerInfo.metro_district?.note}</p>
          <div className="flex gap-4 text-sm">
            <span>Services: {buyerInfo.metro_district?.services?.join(", ")}</span>
            <span>Mill Levy: {buyerInfo.metro_district?.mill_levy}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ListingToolkitPage;
